import React from 'react';
import { observer } from 'mobx-react';
import { Box, Flex, Spinner, Text } from 'core/components';
import CoreSelect from 'core/components/select/Select';
import LabelOptionRenderer from 'app/components/labels/LabelOptionRenderer';
import { isAgentLabelDimension, isDeviceLabelDimension } from '@kentik/ui-shared/query/dimensionUtils';
import { LabelStore } from 'app/stores/label/$labels';
import { FilterState } from './filter.types';
import { useLabelOptions, LabelOptionData } from './useLabelOptions';

interface LabelFilterInputProps {
  filter: FilterState;
  dimensionName: string;
  labelType?: 'synth_agent' | 'synth_test' | 'device';
  $labels?: LabelStore;
  autoFocus?: boolean;
}

function getDefaultLabelType(dimensionName: string): 'synth_agent' | 'synth_test' | 'device' {
  if (isAgentLabelDimension(dimensionName)) {
    return 'synth_agent';
  }
  if (isDeviceLabelDimension(dimensionName)) {
    return 'device';
  }
  return 'synth_test';
}

const LabelValueRenderer = (option: LabelOptionData | null) => {
  if (!option) {
    return <span>Select label...</span>;
  }
  const { label, bg } = option;
  return (
    <Flex alignItems="center" gap="2px" py={0}>
      <Box width={10} height={10} borderRadius="2px" bg={bg}>
        {null}
      </Box>
      <Text ellipsis small>
        {label}
      </Text>
    </Flex>
  );
};

const CompactLabelOptionRenderer = (option: LabelOptionData): JSX.Element => (
  <Box my="-2px">{LabelOptionRenderer(option)}</Box>
);

const LabelFilterInput = observer(({ filter, dimensionName, labelType, $labels, autoFocus }: LabelFilterInputProps) => {
  const resolvedLabelType = labelType ?? getDefaultLabelType(dimensionName);
  const { options, isLoading } = useLabelOptions($labels, resolvedLabelType, true);

  if (isLoading || !options) {
    if (!$labels) {
      return (
        <Text small muted>
          Labels unavailable
        </Text>
      );
    }
    return (
      <Flex alignItems="center" gap={1} p={1}>
        <Spinner size={14} />
        <Box color="muted">Loading labels...</Box>
      </Flex>
    );
  }

  const currentValue = filter.filterValue.getValue();
  const handleChange = (value: string) => {
    filter.filterValue.setValue(value);
  };

  return (
    <Box>
      <CoreSelect
        small
        fill
        showFilter
        autoFocus={autoFocus}
        values={currentValue || ''}
        options={options}
        onChange={handleChange}
        optionFormatter={CompactLabelOptionRenderer}
        valueRenderer={LabelValueRenderer}
        placeholder="Select label..."
        menuWidth="auto"
      />
    </Box>
  );
});

export default LabelFilterInput;
